/**
 * Collection Stock Calculator
 * Calculates total m² stock for collections with 50+ products
 * by fetching paginated data from the collection-stock-data section
 */

class CollectionStockCalculator {
  constructor() {
    this.sectionId = 'collection-stock-data';
  }

  /**
   * Parse stock data from HTML response
   * @param {string} html - HTML response containing JSON data
   * @returns {Object} Parsed stock data
   */
  parseStockData(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const dataElement = doc.querySelector('[data-stock-data]');

    if (!dataElement) {
      return { products: [], hasNextPage: false, nextPageUrl: '' };
    }

    try {
      return JSON.parse(dataElement.textContent);
    } catch (e) {
      console.error('Failed to parse stock data:', e);
      return { products: [], hasNextPage: false, nextPageUrl: '' };
    }
  }

  /**
   * Walk every page of a collection and gather what Liquid cannot see in one
   * render: the full m² total and the warehouses that actually hold stock.
   * @param {string} collectionUrl - The collection URL (e.g., /collections/orion)
   * @returns {Promise<{totalM2: number, locations: string[], pileHeights: number[]}>}
   */
  async calculateCollectionStock(collectionUrl) {
    let totalM2 = 0;
    const locations = new Set();
    const pileHeights = new Set();
    let nextUrl = `${collectionUrl}?section_id=${this.sectionId}`;

    while (nextUrl) {
      try {
        const response = await fetch(nextUrl);
        if (!response.ok) {
          console.error('Failed to fetch stock data:', response.status);
          break;
        }

        const html = await response.text();
        const data = this.parseStockData(html);

        // Sum m² for all products on this page and note their warehouses
        totalM2 += data.products.reduce((sum, product) => sum + (product.m2 || 0), 0);
        data.products.forEach((product) => {
          const name = (product.location || '').trim();
          if (name) locations.add(name);
          const height = parseFloat(product.pileHeight);
          if (height > 0) pileHeights.add(height);
        });

        // Check if there are more pages
        if (data.hasNextPage && data.nextPageUrl) {
          // Append section_id to the next page URL
          const separator = data.nextPageUrl.includes('?') ? '&' : '?';
          nextUrl = `${data.nextPageUrl}${separator}section_id=${this.sectionId}`;
        } else {
          nextUrl = null;
        }
      } catch (error) {
        console.error('Error calculating stock:', error);
        break;
      }
    }

    return {
      totalM2,
      locations: Array.from(locations).sort(),
      pileHeights: Array.from(pileHeights).sort((a, b) => a - b)
    };
  }

  /**
   * Update a stock display element with the calculated value
   * @param {HTMLElement} element - Element to update
   * @param {number} totalM2 - Total m² value
   */
  updateDisplay(element, totalM2) {
    if (!element) return;

    const container = element.closest('.flex, .card-collection__stock');
    const indicator = container?.querySelector('.stock-indicator, .card-collection__stock-dot');

    if (totalM2 <= 0) {
      // Out of stock - show text and red indicator
      element.textContent = element.dataset.oosLabel || 'out of stock';
      // Remove the "m²" suffix if present in parent
      const parent = element.parentElement;
      if (parent && parent.textContent.includes('m²')) {
        parent.innerHTML = '';
        parent.appendChild(element);
      }
      if (indicator) {
        indicator.classList.remove('bg-emerald-400', 'card-collection__stock-dot--in-stock');
        indicator.classList.add('bg-red-400', 'card-collection__stock-dot--out-of-stock');
      }
    } else {
      // In stock - show value with m² and green indicator
      const formattedValue = Math.round(totalM2).toLocaleString();
      element.textContent = formattedValue;
      // Add "m²" suffix if not present
      const parent = element.parentElement;
      if (parent && !parent.textContent.includes('m²')) {
        parent.innerHTML = '';
        parent.appendChild(element);
        parent.appendChild(document.createTextNode('m²'));
      }
      if (indicator) {
        indicator.classList.remove('bg-red-400', 'card-collection__stock-dot--out-of-stock');
        indicator.classList.add('bg-emerald-400', 'card-collection__stock-dot--in-stock');
      }
    }
    element.classList.remove('loading');
  }

  /**
   * Initialize stock calculation for all elements with data-collection-stock attribute
   */
  async init() {
    // Find all elements that need stock calculation
    const stockElements = document.querySelectorAll('[data-collection-stock]');

    if (stockElements.length === 0) return;

    // Process each element
    const calculations = Array.from(stockElements).map(async (element) => {
      const collectionUrl = element.dataset.collectionStock;
      if (!collectionUrl) return;

      // Add loading class
      element.classList.add('loading');

      const { totalM2, locations, pileHeights } = await this.calculateCollectionStock(collectionUrl);

      // Update display
      this.updateDisplay(element, totalM2);

      // Liquid only sees the first 50 products of a collection, so the location
      // list rendered server-side can be incomplete. Now that every page has
      // been read, correct it.
      const item = element.closest('.collection-item');
      if (item) {
        item.dataset.locations = locations.length ? locations.join(',') + ',' : '';
        item.dataset.pileHeights = pileHeights.join(',');
      }
    });

    // Wait for all calculations to complete
    await Promise.all(calculations);

    // Let the collections page rebuild its location filter on the full list
    document.dispatchEvent(new CustomEvent('collection-stock:loaded'));
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const calculator = new CollectionStockCalculator();
  calculator.init();
});
